import { type ResponseData, useInfiniteApiQuery } from '@vrplatform/data';
import type { IconDefinition } from '@vrplatform/icons';
import { formatOwnerName } from '@vrplatform/ui-common';
import { day } from '@vrplatform/utils';
import {
  QUICK_SEARCH_TABS,
  type QuickSearchResultType,
} from './QuickSearch.constants';

export type QuickSearchProductResult = {
  id: string;
  type: QuickSearchResultType;
  label: string;
  description?: string;
  href: string;
  icon: IconDefinition;
  badge: string;
};

const getQuickSearchResultIcon = (type: QuickSearchResultType) =>
  QUICK_SEARCH_TABS.find((tab) => tab.value === type)?.icon ?? 'SearchIcon';

const getQuickSearchResultBadge = (type: QuickSearchResultType) => {
  switch (type) {
    case 'owners':
      return 'Owner';
    case 'listings':
      return 'Listing';
    case 'reservations':
      return 'Reservation';
    case 'expenses':
      return 'Expense';
    case 'deposits':
      return 'Deposit';
    case 'statements':
      return 'Statement';
  }
};

const mapOwnerToQuickSearchResult = (
  owner: ResponseData<'get:/contacts'>['data'][number]
): QuickSearchProductResult => ({
  id: `owner:${owner.id}`,
  type: 'owners',
  label:
    formatOwnerName(owner, { lastNameFirst: false }) ||
    owner.email ||
    owner.shortRef ||
    owner.id,
  description: owner.email ?? undefined,
  href: `/owner/${encodeURIComponent(owner.id)}`,
  icon: getQuickSearchResultIcon('owners'),
  badge: getQuickSearchResultBadge('owners'),
});

const mapListingToQuickSearchResult = (
  listing: ResponseData<'get:/listings'>['data'][number]
): QuickSearchProductResult => ({
  id: `listing:${listing.id}`,
  type: 'listings',
  label: listing.name || listing.title || listing.shortRef || listing.id,
  description: listing.address?.full ?? undefined,
  href: `/listing/${encodeURIComponent(listing.id)}`,
  icon: getQuickSearchResultIcon('listings'),
  badge: getQuickSearchResultBadge('listings'),
});

const mapReservationToQuickSearchResult = (
  reservation: ResponseData<'get:/reservations'>['data'][number]
): QuickSearchProductResult => ({
  id: `reservation:${reservation.id}`,
  type: 'reservations',
  label:
    reservation.guestName ||
    reservation.bookerName ||
    reservation.confirmationCode ||
    reservation.shortRef ||
    reservation.id,
  description: reservation.confirmationCode ?? undefined,
  href: `/reservations?reservation=${encodeURIComponent(reservation.id)}`,
  icon: getQuickSearchResultIcon('reservations'),
  badge: getQuickSearchResultBadge('reservations'),
});

const mapTransactionToQuickSearchResult = (
  transaction: ResponseData<'get:/transactions'>['data'][number],
  type: Extract<QuickSearchResultType, 'deposits' | 'expenses'>
): QuickSearchProductResult => ({
  id: `${type}:${transaction.id}`,
  type,
  label: transaction.description || transaction.shortRef || transaction.id,
  description: transaction.uniqueRef || transaction.shortRef || undefined,
  href: `/${type === 'deposits' ? 'deposit' : 'expense'}/${encodeURIComponent(
    transaction.id
  )}`,
  icon: getQuickSearchResultIcon(type),
  badge: getQuickSearchResultBadge(type),
});

const mapStatementToQuickSearchResult = (
  statement: ResponseData<'get:/statements'>['data'][number]
): QuickSearchProductResult | null => {
  const periodId = statement.ownership?.id;

  if (!periodId) return null;

  return {
    id: `statement:${statement.id ?? `${periodId}:${statement.currency}`}`,
    type: 'statements',
    label:
      formatOwnerName(
        statement.ownership?.members.find((member) => member.contact)
          ?.contact ?? undefined,
        { lastNameFirst: false }
      ) || periodId,
    description: statement.listing.name,
    href: `/statement/${encodeURIComponent(periodId)}?${new URLSearchParams({
      currency: statement.currency,
      date: statement.startAt,
    }).toString()}`,
    icon: getQuickSearchResultIcon('statements'),
    badge: getQuickSearchResultBadge('statements'),
  };
};

export const useQuickSearchResults = ({ query }: { query: string }) =>
  useInfiniteApiQuery<
    {
      counts: Partial<Record<QuickSearchResultType, number>>;
      nextPage?: number | null;
      results: QuickSearchProductResult[];
    },
    { query: string }
  >(
    async (api, signal, args, page) => {
      const limit = 10;
      const requests: Promise<{
        count: number;
        nextPage?: number | null;
        results: QuickSearchProductResult[];
        type: QuickSearchResultType;
      }>[] = [];

      requests.push(
        api
          .GET('/contacts', {
            params: {
              query: {
                limit,
                page,
                search: args.query,
                type: 'owner',
              },
            },
            signal,
          })
          .then((response) => {
            if (response.error) throw response.error;

            return {
              count: response.data.pagination.total,
              nextPage: response.data.pagination.nextPage,
              results: response.data.data.map(mapOwnerToQuickSearchResult),
              type: 'owners' as const,
            };
          })
      );

      requests.push(
        api
          .GET('/listings', {
            params: {
              query: {
                limit,
                page,
                search: args.query,
              },
            },
            signal,
          })
          .then((response) => {
            if (response.error) throw response.error;

            return {
              count: response.data.pagination.total,
              nextPage: response.data.pagination.nextPage,
              results: response.data.data.map(mapListingToQuickSearchResult),
              type: 'listings' as const,
            };
          })
      );

      requests.push(
        api
          .GET('/reservations', {
            params: {
              query: {
                limit,
                page,
                search: args.query,
              },
            },
            signal,
          })
          .then((response) => {
            if (response.error) throw response.error;

            return {
              count: response.data.pagination.total,
              nextPage: response.data.pagination.nextPage,
              results: response.data.data.map(
                mapReservationToQuickSearchResult
              ),
              type: 'reservations' as const,
            };
          })
      );

      requests.push(
        api
          .GET('/transactions', {
            params: {
              query: {
                limit,
                page,
                search: args.query,
                type: 'expense',
              },
            },
            signal,
          })
          .then((response) => {
            if (response.error) throw response.error;

            return {
              count: response.data.pagination.total,
              nextPage: response.data.pagination.nextPage,
              results: response.data.data.map((transaction) =>
                mapTransactionToQuickSearchResult(transaction, 'expenses')
              ),
              type: 'expenses' as const,
            };
          })
      );

      requests.push(
        api
          .GET('/transactions', {
            params: {
              query: {
                limit,
                page,
                search: args.query,
                type: 'deposit',
              },
            },
            signal,
          })
          .then((response) => {
            if (response.error) throw response.error;

            return {
              count: response.data.pagination.total,
              nextPage: response.data.pagination.nextPage,
              results: response.data.data.map((transaction) =>
                mapTransactionToQuickSearchResult(transaction, 'deposits')
              ),
              type: 'deposits' as const,
            };
          })
      );

      requests.push(
        api
          .GET('/statements', {
            params: {
              query: {
                limit,
                month: day().startOf('month').yyyymmdd().slice(0, 7),
                page,
                search: args.query,
              },
            },
            signal,
          })
          .then((response) => {
            if (response.error) throw response.error;

            return {
              count: response.data.pagination.total,
              nextPage: response.data.pagination.nextPage,
              results: response.data.data
                .map(mapStatementToQuickSearchResult)
                .filter(
                  (result): result is QuickSearchProductResult => !!result
                ),
              type: 'statements' as const,
            };
          })
      );

      const responses = await Promise.all(requests);

      return {
        counts: responses.reduce<
          Partial<Record<QuickSearchResultType, number>>
        >(
          (counts, response) => ({
            ...counts,
            [response.type]: response.count,
          }),
          {}
        ),
        nextPage: responses.find((response) => response.nextPage)?.nextPage,
        results: responses.flatMap((response) => response.results),
      };
    },
    {
      hideNotification: true,
      queryGroup: 'owners',
      skip: query.length < 2,
      uniqueQueryKey: 'quick-search-results',
      variables: { query },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    }
  );
